import { Product } from '@atlassiansox/bxpkit-core';
import OriginTracing from '@atlassiansox/origin-tracing';
import React, { createContext, PropsWithChildren, useContext } from 'react';
import { InjectedIntl } from 'react-intl';
import { useSignUpFormConfig } from '../../../hook';
import {
  FeatureFlags,
  LocaleType,
  ProductDataType,
  UserDataType
} from '../../../model';
import { WorkspacesType } from '../../../model/workspaces';
import { DevopsImageUrlsType } from '../types';

import {
  BundleData,
  FREE_EDITION_LABEL_KEY,
  getBundleData,
  getFeaturesList,
  getPageHeaderLabels,
  PageHeaderLabels
} from '../../../utility';
import { accountOperationUrl } from '../utility';

export type DevopsContextProps = {
  readonly labels: {
    [key: string]: string;
  };
  readonly productData: { [key: string]: ProductDataType };
  readonly intl: InjectedIntl;
  readonly enableSkipRecaptcha?: boolean;
  readonly locale: LocaleType;
  readonly devopsImageUrls: DevopsImageUrlsType;
  readonly userInfo?: UserDataType;
  readonly bbworkspaces?: WorkspacesType[];
  readonly featureFlags: FeatureFlags;
};

export type DevopsState = {
  readonly bundleData: BundleData;
  readonly pageHeaderLabels: PageHeaderLabels;
  readonly featuresList: string;
  readonly userInfo?: UserDataType;
  readonly signInAccountUrl: string;
  readonly switchAccountUrl: string;
  readonly switchAccountOrigin: OriginTracing;
  readonly productKeys: Product[];
  readonly intl: InjectedIntl;
  readonly enableSkipRecaptcha: boolean;
  readonly locale: LocaleType;
  readonly devopsImageUrls: DevopsImageUrlsType;
  readonly bbworkspaces?: WorkspacesType[];
  readonly featureFlags: FeatureFlags;
};

export const DevopsContext = createContext<DevopsContextProps>({
  // @ts-ignore
  intl: {},
  labels: {},
  productData: {},
  devopsImageUrls: {
    sidebarImage: '',
    backgroundImage: '',
    envelopeN2N: '',
    successN2E: '',
    codeAwsJsw: ''
  },
  featureFlags: {}
});

export function DevopsProvider({
  children,
  ...props
}: PropsWithChildren<DevopsContextProps>) {
  return (
    <DevopsContext.Provider value={props}>{children}</DevopsContext.Provider>
  );
}

export function useDevopsState(): DevopsState {
  const {
    labels,
    locale,
    productData,
    userInfo,
    devopsImageUrls,
    bbworkspaces,
    intl,
    featureFlags,
    enableSkipRecaptcha = false
  } = useContext(DevopsContext);

  const { bundle: bundleName, product, edition } = useSignUpFormConfig();

  const bundleData = getBundleData({
    activeBundlesMap: productData,
    bundleName,
    productKeys: product,
    edition
  });
  const pageHeaderLabels = getPageHeaderLabels({
    labels,
    bundleData
  });
  const featuresList = getFeaturesList({
    labels,
    bundleData,
    featuresListEditionLabelKey: FREE_EDITION_LABEL_KEY.toLowerCase()
  });
  const { switchAccountUrl, switchAccountOrigin, signInAccountUrl } =
    accountOperationUrl();

  const productKeys = bundleData.products.map((product) => {
    return {
      edition: bundleData.edition,
      imgSource: product.productLogoUrl,
      product: product.productKey,
      serverDownloadContent: null
    };
  });

  return {
    intl,
    pageHeaderLabels,
    bundleData,
    featuresList,
    userInfo,
    signInAccountUrl,
    switchAccountUrl,
    switchAccountOrigin,
    productKeys,
    enableSkipRecaptcha,
    locale,
    devopsImageUrls,
    bbworkspaces,
    featureFlags
  };
}
